// Utilidad para obtener la posición geográfica actual del usuario
// utilizando la Geolocation API disponible en la mayoría de navegadores.

// Tipo de dato simple para exponer latitud y longitud como strings
export type GeoPoint = { lat: string; lng: string };

// Resultado simplificado del reverse geocoding
export type ReverseGeocode = {
  // Nombre de ciudad/municipio (si está disponible)
  ciudad: string | null;
  // Nombre de barrio/suburbio (si está disponible)
  barrio: string | null;
  // Dirección legible (si está disponible)
  direccion: string | null;
};

// Devuelve una Promesa que se resuelve con la posición actual o se rechaza con un error descriptivo
export function getCurrentPosition(): Promise<GeoPoint> {
  // Creamos y devolvemos una nueva Promesa para poder usar async/await donde sea necesario
  return new Promise((resolve, reject) => {
    // Verificamos que el navegador soporte la API de geolocalización
    if (!('geolocation' in navigator)) {
      // Si no hay soporte, rechazamos con un error claro para el usuario
      reject(new Error('Geolocalización no soportada en este navegador'));
      return;
    }

    // Solicitamos la posición actual con alta precisión
    navigator.geolocation.getCurrentPosition(
      // Callback en caso de éxito
      (pos) => {
        // Normalizamos latitud y longitud a strings para almacenar sin pérdida en localStorage
        const lat = Number(pos.coords.latitude).toFixed(6); // 6 decimales para mayor precisión visual
        const lng = Number(pos.coords.longitude).toFixed(6); // 6 decimales para mayor precisión visual
        // Resolvemos la promesa con el objeto de coordenadas
        resolve({ lat, lng });
      },
      // Callback en caso de error (permiso denegado, timeout, etc.)
      (err) => {
        // Rechazamos la promesa propagando un mensaje informativo
        reject(new Error('No se pudo obtener la ubicación: ' + err.message));
      },
      // Opciones recomendadas: alta precisión, timeout razonable, sin usar cache
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

// Variante mejor-esfuerzo: escucha varias lecturas con watchPosition por unos segundos
// y se queda con la de mejor precisión (o la primera si el tiempo expira).
export function getBestPosition(
  // Precisión deseada en metros (resuelve en cuanto se alcance o mejore)
  desiredAccuracyMeters: number = 30,
  // Tiempo máximo de espera en milisegundos para mejorar la precisión
  maxWaitMs: number = 7000
): Promise<GeoPoint> {
  // Devolvemos una Promesa para poder await desde el UI
  return new Promise((resolve, reject) => {
    // Validamos soporte del navegador
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocalización no soportada en este navegador'));
      return;
    }

    // Variables para ir guardando la mejor lectura encontrada
    let bestLat = '' as string; // Latitud con mejor precisión a la fecha
    let bestLng = '' as string; // Longitud con mejor precisión a la fecha
    let bestAcc = Number.POSITIVE_INFINITY as number; // Menor accuracy (mejor) observado
    let settled = false as boolean; // Bandera para evitar resolver dos veces

    // Función utilitaria para completar la promesa y limpiar el watcher
    const finish = (lat: string, lng: string) => {
      // Marcamos resuelta para bloquear ejecuciones posteriores
      settled = true;
      // Devolvemos lat/lng obtenidos en formato string
      const latStr = Number(lat).toFixed(6); // Normalizamos a 6 decimales para consistencia
      const lngStr = Number(lng).toFixed(6); // Normalizamos a 6 decimales para consistencia
      resolve({ lat: latStr, lng: lngStr });
    };

    // Iniciamos un watchPosition para recibir lecturas sucesivas de mayor calidad
    const watchId = navigator.geolocation.watchPosition(
      // Éxito: recibimos una nueva lectura
      (pos) => {
        // Si ya resolvimos, ignoramos lecturas tardías
        if (settled) return;
        // Capturamos lat/lng y precisión en metros
        const lat = String(pos.coords.latitude);
        const lng = String(pos.coords.longitude);
        const acc = Number(pos.coords.accuracy || Number.POSITIVE_INFINITY);
        // Si esta lectura mejora la precisión anterior, la adoptamos como mejor
        if (acc < bestAcc) {
          bestAcc = acc;
          bestLat = lat;
          bestLng = lng;
        }
        // Si ya alcanzamos la precisión deseada, resolvemos inmediatamente
        if (acc <= desiredAccuracyMeters) {
          try { navigator.geolocation.clearWatch(watchId); } catch {}
          finish(lat, lng);
        }
      },
      // Error del watcher (permiso denegado, timeout, etc.)
      (err) => {
        // Cerramos el watcher por seguridad
        try { navigator.geolocation.clearWatch(watchId); } catch {}
        // Si llegamos a tener alguna lectura previa, devolvemos la mejor conocida
        if (!settled && bestLat && bestLng) {
          finish(bestLat, bestLng);
        } else {
          // Si no hubo lecturas, rechazamos con el error original
          reject(new Error('No se pudo obtener la ubicación: ' + err.message));
        }
      },
      // Opciones de máxima precisión y sin reutilizar cache
      { enableHighAccuracy: true, timeout: maxWaitMs, maximumAge: 0 }
    );

    // Programamos un corte duro por tiempo máximo para devolver la mejor lectura disponible
    setTimeout(() => {
      // Si ya resolvimos antes, no hacemos nada
      if (settled) return;
      // Limpiamos el watcher para dejar de escuchar lecturas
      try { navigator.geolocation.clearWatch(watchId); } catch {}
      // Si tenemos alguna lectura, completamos con la mejor; si no, intentamos un getCurrentPosition rápido
      if (bestLat && bestLng) {
        finish(bestLat, bestLng);
      } else {
        // Fallback a una lectura única con el mismo set de opciones
        navigator.geolocation.getCurrentPosition(
          (pos) => finish(String(pos.coords.latitude), String(pos.coords.longitude)),
          (err) => reject(new Error('No se pudo obtener la ubicación: ' + err.message)),
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      }
    }, maxWaitMs);
  });
}

// Llama a Nominatim (OpenStreetMap) para hacer reverse geocoding y obtener ciudad/barrio/dirección
export async function reverseGeocode(lat: string, lng: string): Promise<ReverseGeocode> {
  // Construimos la URL con parámetros adecuados y idioma español
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&format=jsonv2&addressdetails=1&accept-language=es&_t=${Date.now()}`; // _t evita caches intermedios

  // Realizamos la solicitud con fetch (Nominatim permite CORS desde el navegador)
  const resp = await fetch(url, {
    // El navegador incluirá automáticamente el Referer; no podemos establecer User-Agent manualmente
    method: 'GET',
  });

  // Validamos respuesta HTTP
  if (!resp.ok) {
    // Si hay un error HTTP, lanzamos una excepción con el código de estado
    throw new Error(`Reverse geocoding falló (${resp.status})`);
  }

  // Parseamos la respuesta JSON
  const data: any = await resp.json();
  const address = data?.address || {};

  // Tomamos la mejor coincidencia para ciudad usando campos comunes de OSM (más amplia)
  let ciudad: string | null =
    address.city || address.town || address.village || address.municipality || address.state_district || address.state || address.county || null;

  // Tomamos la mejor coincidencia para barrio/suburbio (más amplia)
  let barrio: string | null =
    address.suburb || address.neighbourhood || address.quarter || address.city_district || address.residential || address.hamlet || address.ward || address.district || address.borough || address.township || address.locality || null;

  // Extraemos una dirección legible si está disponible
  const direccion: string | null = data?.display_name || null;

  // Si no se obtuvo barrio, intentamos inferirlo a partir de display_name
  if (!barrio && direccion) {
    try {
      const partes = direccion.split(',').map((s: string) => s.trim());
      // Heurística: si tenemos ciudad conocida, tomamos el elemento previo a la ciudad
      if (ciudad) {
        const idx = partes.findIndex((p: string) => p.toLowerCase() === String(ciudad).toLowerCase());
        if (idx > 0) {
          barrio = partes[idx - 1];
        }
      }
      // Si aún no hay barrio, usamos el segundo elemento como aproximación de barrio/zona
      if (!barrio && partes.length >= 2) {
        barrio = partes[1];
      }
    } catch {
      // Ignorar errores de parseo
    }
  }

  // Devolvemos el objeto simplificado
  return { ciudad, barrio, direccion };
}
